import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart3, Users, BookOpen, LogOut, Plus, Edit, Trash2, 
  Building2, GraduationCap, CreditCard, Award, TrendingUp,
  ArrowLeft, Upload, FileText, Eye, ExternalLink, Scale, Save,
  Mail, Send, UserPlus, ListFilter, ShoppingCart, Sparkles, Loader2
} from 'lucide-react';
import LegalDocumentRow from './admin/LegalDocumentRow';
import SalesOrdersPanel from './admin/SalesOrdersPanel';
import TrainingWishesTab from './admin/TrainingWishesTab';
import AdminAnalyticsPanel from './admin/AdminAnalyticsPanel';
import AdminVideoIntrosPanel from './admin/AdminVideoIntrosPanel';
import TrackingSettingsTab from './admin/TrackingSettingsTab';
import CompaniesTab from './admin/CompaniesTab';
import EnrollmentsTab from './admin/EnrollmentsTab';
import LessonVideoUrlManager from '../components/LessonVideoUrlManager';
import { CourseContentEditor } from './admin/CourseContentEditor';
import { BlankCourseDialog } from './admin/BlankCourseDialog';

import { apiFetch } from '../services/api';
const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { company, logout } = useAuth();
  const fileInputRef = useRef(null);
  const courseFileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [trainingContents, setTrainingContents] = useState([]);
  const [legalPages, setLegalPages] = useState({});
  const [emailContacts, setEmailContacts] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [allCertificates, setAllCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isAddingCourse, setIsAddingCourse] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showBlankDialog, setShowBlankDialog] = useState(false);
  const [isUploadingTraining, setIsUploadingTraining] = useState(false);
  const [selectedCourseForUpload, setSelectedCourseForUpload] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCourseFile, setSelectedCourseFile] = useState(null);
  const [uploadingCourseId, setUploadingCourseId] = useState(null);
  
  // Micro-Learning Seed
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seedRunning, setSeedRunning] = useState(false);
  
  // Legal pages editing
  const [editingLegal, setEditingLegal] = useState(null);
  const [legalContent, setLegalContent] = useState('');
  
  // Email campaign
  const [isSendingCampaign, setIsSendingCampaign] = useState(false);
  const [campaignSubject, setCampaignSubject] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [newContact, setNewContact] = useState({ email: '', name: '', company: '' });
  
  // Email template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    duration: '',
    price: '',
    category: '',
    videoUrl: '',
    digistoreProductId: '',
    questions: []
  });

  useEffect(() => {
    loadData();
  }, []);

  // Deep-Link-Support: `?edit-course=<moduleId>` öffnet automatisch den
  // Edit-Dialog. Wird u. a. vom CoursePlayer-Admin-Preview verwendet
  // ("Lektion bearbeiten"-Button oben). Ohne diesen Handler landete der
  // User zuvor kommentarlos auf der Kursübersicht.
  useEffect(() => {
    const editId = searchParams.get('edit-course');
    if (!editId || courses.length === 0 || editingCourse) return;
    const match = courses.find(c => c.id === editId || c._id === editId || c.moduleId === editId);
    if (match) {
      setEditingCourse({ ...match });
      // Query-Param leeren, damit der Dialog beim manuellen Schließen nicht
      // wieder öffnet (aber `lesson` behalten, falls Editor Deep-Link nutzt).
      const next = new URLSearchParams(searchParams);
      next.delete('edit-course');
      setSearchParams(next, { replace: true });
    }
  }, [courses, searchParams, editingCourse, setSearchParams]);

  const loadData = async () => {
    try {
      const headers = { };
      
      // Load stats
      const statsRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { headers });
      const statsData = await statsRes.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }
      
      // Load courses
      const coursesRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses`);
      const coursesData = await coursesRes.json();
      if (coursesData.success) {
        setCourses(coursesData.courses.map(c => ({ ...c, id: c.id || c._id })));
      }
      
      // Load companies
      const companiesRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/companies`, { headers });
      const companiesData = await companiesRes.json();
      if (companiesData.success) {
        setCompanies(companiesData.companies);
      }
      
      // Load enrollments
      const enrollmentsRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/enrollments`, { headers });
      const enrollmentsData = await enrollmentsRes.json();
      if (enrollmentsData.success) {
        setEnrollments(enrollmentsData.enrollments);
      }
      
      // Load training contents
      const trainingRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/training/list`, { headers });
      const trainingData = await trainingRes.json();
      if (trainingData.success) {
        setTrainingContents(trainingData.contents || []);
      }
      
      // Load legal pages
      const legalRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal`, { headers });
      const legalData = await legalRes.json();
      if (legalData.success) {
        const pagesMap = {};
        legalData.pages.forEach(p => {
          pagesMap[p.type] = p;
        });
        setLegalPages(pagesMap);
      }
      
      // Load email contacts
      const contactsRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/contacts`, { headers });
      const contactsData = await contactsRes.json();
      if (contactsData.success) {
        setEmailContacts(contactsData.contacts || []);
      }
      
      // Load email templates
      const templatesRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/templates`, { headers });
      const templatesData = await templatesRes.json();
      if (templatesData.success) {
        setEmailTemplates(templatesData.templates || []);
      }
      
      // Load all certificates (admin only)
      const certsRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/admin/all`, { headers });
      const certsData = await certsRes.json();
      if (certsData.success) {
        setAllCertificates(certsData.certificates || []);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading admin data:', error);
      setLoading(false);
      toast({
        title: "Fehler",
        description: "Daten konnten nicht geladen werden",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Email functions
  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/contacts/upload`, {
        method: 'POST',
        headers: { },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Erfolg",
          description: data.message
        });
        loadData();
      } else {
        throw new Error(data.detail || 'Fehler beim Upload');
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleAddContact = async () => {
    if (!newContact.email) {
      toast({ title: "Fehler", description: "E-Mail ist erforderlich", variant: "destructive" });
      return;
    }
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify(newContact)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Erfolg", description: "Kontakt hinzugefügt" });
        setNewContact({ email: '', name: '', company: '' });
        loadData();
      } else {
        throw new Error(data.detail || 'Fehler');
      }
    } catch (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Kontakt wirklich löschen?')) return;
    try {
      await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { }
      });
      loadData();
    } catch (error) {
      toast({ title: "Fehler", description: "Konnte nicht gelöscht werden", variant: "destructive" });
    }
  };

  const handleSendCampaign = async (sendToAll = false) => {
    if (!campaignSubject || !selectedTemplate) {
      toast({ title: "Fehler", description: "Betreff und Vorlage sind erforderlich", variant: "destructive" });
      return;
    }
    
    if (!sendToAll && !testEmail) {
      toast({ title: "Fehler", description: "Bitte Test-E-Mail angeben", variant: "destructive" });
      return;
    }
    
    setIsSendingCampaign(true);
    try {
      const formData = new FormData();
      formData.append('subject', campaignSubject);
      formData.append('template_id', selectedTemplate);
      if (testEmail && !sendToAll) {
        formData.append('test_email', testEmail);
      }
      formData.append('send_to_all', sendToAll.toString());
      
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/send`, {
        method: 'POST',
        headers: { },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Erfolg! ✉️", description: data.message });
        if (sendToAll) {
          setCampaignSubject('');
          setSelectedTemplate('');
        }
      } else {
        throw new Error(data.detail || 'Fehler beim Senden');
      }
    } catch (error) {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    }
    
    setIsSendingCampaign(false);
  };

  const handleUploadCourseHtml = async (courseId, file) => {
    if (!file) return;
    
    try {
      const course = courses.find(c => (c.id || c._id) === courseId);
      
      const formData = new FormData();
      formData.append('course_id', courseId);
      formData.append('title', course?.title || 'Kursinhalt');
      formData.append('file', file);
      
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/training/upload`, {
        method: 'POST',
        headers: { },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Erfolg",
          description: "HTML-Schulung erfolgreich hochgeladen"
        });
        setUploadingCourseId(null);
        setSelectedCourseFile(null);
        loadData();
      } else {
        throw new Error(data.detail || 'Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Error uploading course HTML:', error);
      toast({
        title: "Fehler",
        description: error.message || "Schulung konnte nicht hochgeladen werden",
        variant: "destructive"
      });
    }
  };

  const handleSaveLegalPage = async (pageType) => {
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/${pageType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({ content: legalContent })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Erfolg",
          description: `${pageType.charAt(0).toUpperCase() + pageType.slice(1)} erfolgreich gespeichert`
        });
        setEditingLegal(null);
        loadData();
      } else {
        throw new Error(data.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving legal page:', error);
      toast({
        title: "Fehler",
        description: error.message || "Seite konnte nicht gespeichert werden",
        variant: "destructive"
      });
    }
  };

  const handleUploadTraining = async () => {
    if (!selectedCourseForUpload || !uploadTitle || !selectedFile) {
      toast({
        title: "Fehler",
        description: "Bitte alle Felder ausfüllen und eine Datei auswählen",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('course_id', selectedCourseForUpload);
      formData.append('title', uploadTitle);
      formData.append('file', selectedFile);
      
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/training/upload`, {
        method: 'POST',
        headers: {
          },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Erfolg",
          description: "Schulungsinhalt erfolgreich hochgeladen"
        });
        setIsUploadingTraining(false);
        setSelectedCourseForUpload('');
        setUploadTitle('');
        setSelectedFile(null);
        loadData(); // Refresh
      } else {
        throw new Error(data.detail || 'Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Error uploading training:', error);
      toast({
        title: "Fehler",
        description: error.message || "Schulungsinhalt konnte nicht hochgeladen werden",
        variant: "destructive"
      });
    }
  };

  const handleDeleteTraining = async (contentId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Schulungsinhalt löschen möchten?')) {
      return;
    }
    
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/training/${contentId}`, {
        method: 'DELETE',
        headers: {
          }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Erfolg",
          description: "Schulungsinhalt gelöscht"
        });
        loadData();
      } else {
        throw new Error(data.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error.message || "Schulungsinhalt konnte nicht gelöscht werden",
        variant: "destructive"
      });
    }
  };

  // Email Template Functions
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateContent(template.content || '');
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setIsSavingTemplate(true);
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({ content: templateContent })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Erfolg",
          description: "Vorlage erfolgreich gespeichert"
        });
        setEditingTemplate(null);
        setTemplateContent('');
        loadData();
      } else {
        throw new Error(data.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error.message || "Vorlage konnte nicht gespeichert werden",
        variant: "destructive"
      });
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handlePreviewTemplate = (templateId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/email/templates/${templateId}/preview`, '_blank');
  };

  const handlePreviewTraining = (contentId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/training/view/${contentId}`, '_blank');
  };

  const handleCreateCourse = async () => {
    if (!newCourse.title || !newCourse.price) {
      toast({
        title: "Fehler",
        description: "Titel und Preis sind erforderlich",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newCourse,
          price: parseFloat(newCourse.price)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCourses([...courses, { ...data.course, id: data.course._id }]);
        setNewCourse({
          title: '',
          description: '',
          duration: '',
          price: '',
          category: '',
          videoUrl: '',
          questions: []
        });
        setIsAddingCourse(false);
        toast({
          title: "Erfolg",
          description: "Kurs erfolgreich erstellt"
        });
        loadData(); // Refresh stats
      } else {
        throw new Error(data.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      toast({
        title: "Fehler",
        description: error.message || "Kurs konnte nicht erstellt werden",
        variant: "destructive"
      });
    }
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse) return;
    
    try {
      const courseId = editingCourse.id || editingCourse._id;
      
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/courses/${courseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingCourse.title,
          description: editingCourse.description,
          duration: editingCourse.duration,
          price: parseFloat(editingCourse.price),
          category: editingCourse.category,
          videoUrl: editingCourse.videoUrl,
          digistoreProductId: editingCourse.digistoreProductId || ''
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCourses(courses.map(c => 
          (c.id === courseId || c._id === courseId) 
            ? { ...data.course, id: data.course._id } 
            : c
        ));
        setEditingCourse(null);
        toast({
          title: "Erfolg",
          description: "Kurs erfolgreich aktualisiert"
        });
      } else {
        throw new Error(data.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating course:', error);
      toast({
        title: "Fehler",
        description: error.message || "Kurs konnte nicht aktualisiert werden",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Kurs löschen möchten?')) {
      return;
    }
    
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/courses/${courseId}`, {
        method: 'DELETE',
        headers: {
          }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCourses(courses.filter(c => c.id !== courseId && c._id !== courseId));
        toast({
          title: "Erfolg",
          description: "Kurs erfolgreich gelöscht"
        });
        loadData(); // Refresh stats
      } else {
        throw new Error(data.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      toast({
        title: "Fehler",
        description: error.message || "Kurs konnte nicht gelöscht werden",
        variant: "destructive"
      });
    }
  };

  // Micro-Learning Content Seed (Haupt-Admin only)
  const handleSeedMicroLearning = async () => {
    setSeedRunning(true);
    try {
      const res = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seed-micro-learning`, {
        method: 'POST',
        headers: { }
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Seed konnte nicht ausgeführt werden');
      }
      toast({
        title: 'Micro-Learning aktualisiert',
        description: `${data.courses} Kurse mit ${data.lessons} Lektionen erfolgreich aktualisiert!` });
      setSeedDialogOpen(false);
      loadData(); // Kurse-Tabelle neu laden
    } catch (err) {
      toast({
        title: 'Fehler beim Seeden',
        description: err.message,
        variant: 'destructive' });
    } finally {
      setSeedRunning(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "yellow" }) => (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
      <CardContent className="p-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-400 text-sm">{title}</p>
            <p className={`text-3xl font-bold ${color === "green" ? "text-green-500" : "text-white"}`}>
              {value}
            </p>
          </div>
          <Icon className={`h-10 w-10 ${color === "green" ? "text-green-500" : "text-yellow-500"}`} />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Header */}
      <header className="border-b border-yellow-600/20 bg-black/40 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/assets/smartvolt-logo.png" alt="Smartvolt Academy" className="h-12" />
            </div>
            <span className="text-yellow-500 font-semibold">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10"
              onClick={() => navigate('/admin/insights')}
              data-testid="header-insights-link"
            >
              <BarChart3 className="h-4 w-4 mr-2" /> Live-Insights
            </Button>
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10"
              onClick={() => navigate('/admin/orders')}
              data-testid="header-orders-link"
            >
              <ShoppingCart className="h-4 w-4 mr-2" /> Bestellungen-Monitor
            </Button>
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Zurück zum Dashboard
            </Button>
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10" 
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20">
            <p className="text-white text-xl">Laden...</p>
          </div>
        ) : (
          <>
            {/* Statistics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
              <StatCard title="Firmen" value={stats?.totalCompanies || 0} icon={Building2} />
              <StatCard title="Mitarbeiter" value={stats?.totalEmployees || 0} icon={Users} />
              <StatCard title="Kurse" value={stats?.totalCourses || 0} icon={BookOpen} />
              <StatCard title="Anmeldungen" value={stats?.totalEnrollments || 0} icon={GraduationCap} />
              <StatCard 
                title="Umsatz" 
                value={`€${stats?.totalRevenue?.toFixed(2) || '0.00'}`} 
                icon={TrendingUp} 
                color="green"
              />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard title="Abgeschlossen" value={stats?.completedEnrollments || 0} icon={Award} />
              <StatCard title="Bezahlt" value={stats?.paidEnrollments || 0} icon={CreditCard} />
              <StatCard title="Ausstehend" value={stats?.pendingPayments || 0} icon={CreditCard} />
              <StatCard title="Zertifikate" value={stats?.totalCertificates || 0} icon={Award} />
            </div>

            {/* Tabs */}
            <Tabs defaultValue="courses" className="w-full">
              <TabsList
                className="bg-gray-900 border-yellow-600/20 mb-6 flex w-full overflow-x-auto flex-nowrap justify-start scrollbar-thin scrollbar-thumb-yellow-500/40 scrollbar-track-transparent"
                data-testid="admin-tabs-list"
              >
                <TabsTrigger value="courses" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500" data-testid="tab-courses">
                  Kurse verwalten
                </TabsTrigger>
                <TabsTrigger value="training" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
                  Schulungsinhalte
                </TabsTrigger>
                <TabsTrigger value="email" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
                  E-Mail Marketing
                </TabsTrigger>
                <TabsTrigger value="certificates" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
                  Zertifikate
                </TabsTrigger>
                <TabsTrigger value="legal" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
                  Rechtliches
                </TabsTrigger>
                <TabsTrigger value="tracking" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500" data-testid="tab-tracking">
                  Tracking &amp; Cookies
                </TabsTrigger>
                <TabsTrigger value="companies" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
                  Firmen
                </TabsTrigger>
                <TabsTrigger value="enrollments" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
                  Anmeldungen
                </TabsTrigger>
                <TabsTrigger value="sales" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500" data-testid="tab-sales">
                  Bestellungen &amp; Umsätze
                </TabsTrigger>
                <TabsTrigger value="wishes" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500" data-testid="tab-wishes">
                  Schulungswünsche
                </TabsTrigger>
                <TabsTrigger value="analytics" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500" data-testid="tab-analytics">
                  Analytics &amp; Drop-offs
                </TabsTrigger>
                <TabsTrigger value="video-intros" className="shrink-0 data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500" data-testid="tab-video-intros">
                  Sora-2 Video-Intros
                </TabsTrigger>
              </TabsList>

              {/* Courses Tab */}
              <TabsContent value="courses">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
                  <CardHeader>
                    <div className="flex justify-between items-center gap-3 flex-wrap">
                      <div>
                        <CardTitle className="text-white">Kursverwaltung</CardTitle>
                        <CardDescription className="text-gray-400">
                          Kurse erstellen, bearbeiten und löschen
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* YouTube-Videos pro Lektion verwalten */}
                        <LessonVideoUrlManager />
                        {/* Micro-Learning Seed-Button (unauffällig, links vom 'Neuer Kurs') */}
                        <Dialog open={seedDialogOpen} onOpenChange={(v) => !seedRunning && setSeedDialogOpen(v)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/10 hover:text-yellow-200"
                              data-testid="seed-micro-learning-btn"
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              Micro-Learning Datenbank aktualisieren / Seeden
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-gray-900 border-yellow-600/30 max-w-md" data-testid="seed-dialog">
                            <DialogHeader>
                              <DialogTitle className="text-white flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-yellow-400" />
                                Micro-Learning-Content aktualisieren
                              </DialogTitle>
                              <DialogDescription className="text-gray-300 pt-2 leading-relaxed">
                                Möchtest du die 10 Kern-Kurse für Arbeitssicherheit, VDE und DGUV auf den neuesten Stand bringen?
                              </DialogDescription>
                            </DialogHeader>
                            <div className="text-xs text-gray-500 mt-2 space-y-1 border-l-2 border-yellow-500/40 pl-3">
                              <p>• Idempotent: bestehende Kurse werden aktualisiert, keine Duplikate.</p>
                              <p>• Betroffen: <code className="text-yellow-300">course_modules</code>, <code className="text-yellow-300">lessons</code>, <code className="text-yellow-300">courses</code>.</p>
                              <p>• Nur der Haupt-Admin darf diesen Vorgang ausführen.</p>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                              <Button
                                variant="ghost"
                                onClick={() => setSeedDialogOpen(false)}
                                disabled={seedRunning}
                                className="text-gray-400 hover:text-white"
                                data-testid="seed-cancel-btn"
                              >
                                Abbrechen
                              </Button>
                              <Button
                                onClick={handleSeedMicroLearning}
                                disabled={seedRunning}
                                className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-black font-semibold hover:from-yellow-400 hover:to-yellow-300"
                                data-testid="seed-confirm-btn"
                              >
                                {seedRunning ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Seede …</>
                                ) : (
                                  <><Sparkles className="h-4 w-4 mr-2" /> Jetzt aktualisieren</>
                                )}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>

                        <Button
                          className="bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 font-semibold"
                          onClick={() => setShowBlankDialog(true)}
                          data-testid="create-blank-course-btn"
                          title="Erstellt Modul mit 3 leeren Lektions-Slots + Quiz-Vorlage"
                        >
                          <Sparkles className="h-4 w-4 mr-2" /> Neuer Blanko-Kurs
                        </Button>

                        <BlankCourseDialog
                          open={showBlankDialog}
                          onOpenChange={setShowBlankDialog}
                          onCreated={async (moduleId, title) => {
                            await loadData();
                            // Editor direkt öffnen: finde den frisch geladenen Kurs
                            setTimeout(() => {
                              const fresh = { id: moduleId, moduleId, title, description: '', price: 0, category: 'allgemein', duration: '30 Min' };
                              setEditingCourse(fresh);
                            }, 400);
                          }}
                        />

                        <Dialog open={isAddingCourse} onOpenChange={setIsAddingCourse}>
                          <DialogTrigger asChild>
                            <Button className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400">
                              <Plus className="h-4 w-4 mr-2" /> Neuer Kurs
                            </Button>
                          </DialogTrigger>
                        <DialogContent className="bg-gray-900 border-yellow-600/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-white">Neuen Kurs erstellen</DialogTitle>
                            <DialogDescription className="text-gray-400">
                              Füllen Sie alle Felder aus
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-gray-300">Titel *</Label>
                              <Input
                                value={newCourse.title}
                                onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
                                className="bg-gray-800 border-yellow-600/20 text-white"
                                placeholder="z.B. Hochspannungstechnik Grundlagen"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Beschreibung</Label>
                              <Textarea
                                value={newCourse.description}
                                onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
                                className="bg-gray-800 border-yellow-600/20 text-white"
                                placeholder="Kursbeschreibung..."
                                rows={3}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-gray-300">Preis (€) *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={newCourse.price}
                                  onChange={(e) => setNewCourse({...newCourse, price: e.target.value})}
                                  className="bg-gray-800 border-yellow-600/20 text-white"
                                  placeholder="49.99"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-300">Dauer</Label>
                                <Input
                                  value={newCourse.duration}
                                  onChange={(e) => setNewCourse({...newCourse, duration: e.target.value})}
                                  className="bg-gray-800 border-yellow-600/20 text-white"
                                  placeholder="z.B. 90 Minuten"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-gray-300">Kategorie</Label>
                              <Input
                                value={newCourse.category}
                                onChange={(e) => setNewCourse({...newCourse, category: e.target.value})}
                                className="bg-gray-800 border-yellow-600/20 text-white"
                                placeholder="z.B. Sicherheit, Qualifikation"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Video URL (YouTube Embed)</Label>
                              <Input
                                value={newCourse.videoUrl}
                                onChange={(e) => setNewCourse({...newCourse, videoUrl: e.target.value})}
                                className="bg-gray-800 border-yellow-600/20 text-white"
                                placeholder="https://www.youtube.com/embed/..."
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">Digistore24 Produkt-ID</Label>
                              <Input
                                value={newCourse.digistoreProductId}
                                onChange={(e) => setNewCourse({...newCourse, digistoreProductId: e.target.value})}
                                className="bg-gray-800 border-yellow-600/20 text-white"
                                placeholder="z.B. 654378"
                              />
                              <p className="text-gray-500 text-xs mt-1">Die Produkt-ID aus Ihrem Digistore24-Konto</p>
                            </div>
                            <Button 
                              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                              onClick={handleCreateCourse}
                            >
                              Kurs erstellen
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {courses.map(course => (
                        <div key={course.id || course._id} className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded">
                                  {course.category}
                                </span>
                                {course.hasTrainingContent && (
                                  <span className="text-xs font-semibold text-green-500 bg-green-500/10 px-2 py-1 rounded">
                                    HTML vorhanden
                                  </span>
                                )}
                                {course.digistoreProductId && (
                                  <span className="text-xs font-semibold text-blue-500 bg-blue-500/10 px-2 py-1 rounded">
                                    Digistore: {course.digistoreProductId}
                                  </span>
                                )}
                              </div>
                              <h3 className="text-white font-semibold mt-2">{course.title}</h3>
                              <p className="text-gray-400 text-sm mt-1">{course.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className="text-gray-500">{course.duration}</span>
                                <span className="text-yellow-500 font-bold">€{course.price}</span>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap justify-end">
                              {/* Admin-Vorschau: IMMER anzeigen — CoursePlayer routet
                                  Legacy-Kurse zu ihrem verlinkten V2-Modul (siehe
                                  link_legacy_courses.py Startup-Migration). */}
                              <Button
                                size="sm"
                                className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-semibold hover:from-yellow-400 hover:to-amber-400 shadow-md shadow-yellow-500/30 border border-yellow-400/50"
                                onClick={() => {
                                  const targetId = course.moduleId || course.id || course._id;
                                  window.open(`/kurs/${targetId}?admin=1`, '_blank', 'noopener,noreferrer');
                                }}
                                data-testid={`self-test-btn-${course.moduleId || course.id || course._id}`}
                                title="Öffnet den echten CoursePlayer mit Lisa-TTS, Quiz und Fortschritt — genau wie deine Mitarbeiter ihn sehen."
                              >
                                <Eye className="h-4 w-4 mr-1" /> Als Teilnehmer durchspielen
                              </Button>
                              {/* HTML Upload Button */}
                              <div className="relative">
                                <input
                                  type="file"
                                  accept=".html,.htm"
                                  className="hidden"
                                  id={`html-upload-${course.id || course._id}`}
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      handleUploadCourseHtml(course.id || course._id, e.target.files[0]);
                                    }
                                  }}
                                />
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                                  onClick={() => document.getElementById(`html-upload-${course.id || course._id}`).click()}
                                >
                                  <Upload className="h-4 w-4 mr-1" /> HTML
                                </Button>
                              </div>
                              <Dialog open={editingCourse?.id === course.id || editingCourse?._id === course._id} onOpenChange={(open) => !open && setEditingCourse(null)}>
                                <DialogTrigger asChild>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                                    onClick={() => setEditingCourse({...course})}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="bg-gray-900 border-yellow-600/20 max-w-5xl max-h-[92vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle className="text-white">Kurs bearbeiten</DialogTitle>
                                  </DialogHeader>
                                  {editingCourse && (
                                    <div className="space-y-4 mt-4">
                                      <div>
                                        <Label className="text-gray-300">Titel</Label>
                                        <Input
                                          value={editingCourse.title}
                                          onChange={(e) => setEditingCourse({...editingCourse, title: e.target.value})}
                                          className="bg-gray-800 border-yellow-600/20 text-white"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-gray-300">Beschreibung</Label>
                                        <Textarea
                                          value={editingCourse.description}
                                          onChange={(e) => setEditingCourse({...editingCourse, description: e.target.value})}
                                          className="bg-gray-800 border-yellow-600/20 text-white"
                                          rows={3}
                                        />
                                      </div>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div>
                                          <Label className="text-gray-300">Preis (€)</Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={editingCourse.price}
                                            onChange={(e) => setEditingCourse({...editingCourse, price: e.target.value})}
                                            className="bg-gray-800 border-yellow-600/20 text-white"
                                          />
                                        </div>
                                        <div>
                                          <Label className="text-gray-300">Dauer</Label>
                                          <Input
                                            value={editingCourse.duration}
                                            onChange={(e) => setEditingCourse({...editingCourse, duration: e.target.value})}
                                            className="bg-gray-800 border-yellow-600/20 text-white"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <Label className="text-gray-300">Kategorie</Label>
                                        <Input
                                          value={editingCourse.category}
                                          onChange={(e) => setEditingCourse({...editingCourse, category: e.target.value})}
                                          className="bg-gray-800 border-yellow-600/20 text-white"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-gray-300">Video URL</Label>
                                        <Input
                                          value={editingCourse.videoUrl}
                                          onChange={(e) => setEditingCourse({...editingCourse, videoUrl: e.target.value})}
                                          className="bg-gray-800 border-yellow-600/20 text-white"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-gray-300">Digistore24 Produkt-ID</Label>
                                        <Input
                                          value={editingCourse.digistoreProductId || ''}
                                          onChange={(e) => setEditingCourse({...editingCourse, digistoreProductId: e.target.value})}
                                          className="bg-gray-800 border-yellow-600/20 text-white"
                                          placeholder="z.B. 654378"
                                        />
                                        <p className="text-gray-500 text-xs mt-1">Die Produkt-ID aus Ihrem Digistore24-Konto</p>
                                      </div>
                                      <Button 
                                        className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                                        onClick={handleUpdateCourse}
                                      >
                                        Änderungen speichern
                                      </Button>

                                      {/* Content-Editor: nur bei V2-Modul (moduleId existiert) */}
                                      {editingCourse.moduleId && (
                                        <div className="mt-8 pt-6 border-t border-yellow-500/20">
                                          <CourseContentEditor
                                            moduleId={editingCourse.moduleId}
                                            moduleTitle={editingCourse.title}
                                            contentStatus={editingCourse.content_status}
                                            onClose={() => setEditingCourse(null)}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </DialogContent>
                              </Dialog>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                onClick={() => handleDeleteCourse(course.id || course._id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {courses.length === 0 && (
                        <p className="text-gray-400 text-center py-8">Keine Kurse vorhanden</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Training Content Tab */}
              <TabsContent value="training">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle className="text-white">Schulungsinhalte</CardTitle>
                        <CardDescription className="text-gray-400">
                          HTML-Schulungen hochladen und verwalten
                        </CardDescription>
                      </div>
                      <Dialog open={isUploadingTraining} onOpenChange={setIsUploadingTraining}>
                        <DialogTrigger asChild>
                          <Button className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400">
                            <Upload className="h-4 w-4 mr-2" /> Schulung hochladen
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-gray-900 border-yellow-600/20 max-w-lg">
                          <DialogHeader>
                            <DialogTitle className="text-white">HTML-Schulung hochladen</DialogTitle>
                            <DialogDescription className="text-gray-400">
                              Laden Sie eine HTML-Datei für einen Kurs hoch
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label className="text-gray-300">Kurs auswählen *</Label>
                              <select
                                value={selectedCourseForUpload}
                                onChange={(e) => setSelectedCourseForUpload(e.target.value)}
                                className="w-full bg-gray-800 border border-yellow-600/20 text-white rounded-md p-2"
                              >
                                <option value="">Kurs wählen...</option>
                                {courses.map(course => (
                                  <option key={course.id || course._id} value={course.id || course._id}>
                                    {course.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <Label className="text-gray-300">Titel der Schulung *</Label>
                              <Input
                                value={uploadTitle}
                                onChange={(e) => setUploadTitle(e.target.value)}
                                className="bg-gray-800 border-yellow-600/20 text-white"
                                placeholder="z.B. Modul 1 - Einführung"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-300">HTML-Datei *</Label>
                              <div className="mt-2">
                                <input
                                  type="file"
                                  accept=".html,.htm"
                                  ref={fileInputRef}
                                  onChange={(e) => setSelectedFile(e.target.files[0])}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full border-yellow-600/20 text-gray-300 hover:bg-yellow-500/10"
                                  onClick={() => fileInputRef.current?.click()}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  {selectedFile ? selectedFile.name : 'Datei auswählen...'}
                                </Button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Nur .html oder .htm Dateien erlaubt</p>
                            </div>
                            <Button 
                              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                              onClick={handleUploadTraining}
                            >
                              <Upload className="h-4 w-4 mr-2" /> Hochladen
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {trainingContents.length > 0 ? trainingContents.map(content => (
                        <div key={content._id} className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                          <div className="flex justify-between items-start">
                            <div className="flex gap-4 items-start">
                              <FileText className="h-10 w-10 text-yellow-500" />
                              <div>
                                <h3 className="text-white font-semibold">{content.title}</h3>
                                <p className="text-gray-400 text-sm">Kurs: {content.courseTitle || 'Unbekannt'}</p>
                                <p className="text-gray-500 text-xs mt-1">
                                  Datei: {content.originalFilename}
                                </p>
                                <p className="text-gray-500 text-xs">
                                  Hochgeladen: {new Date(content.uploadedAt).toLocaleDateString('de-DE')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/training/view/${content._id}`, '_blank')}
                              >
                                <Eye className="h-4 w-4 mr-1" /> Ansehen
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                                onClick={() => handleDeleteTraining(content._id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="text-center py-12">
                          <FileText className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                          <p className="text-gray-400">Noch keine Schulungsinhalte hochgeladen</p>
                          <p className="text-gray-500 text-sm mt-2">Klicken Sie auf "Schulung hochladen" um zu beginnen</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Email Marketing Tab */}
              <TabsContent value="email">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Mail className="h-5 w-5 text-yellow-500" />
                      E-Mail Marketing
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Kontaktlisten verwalten und Kampagnen versenden
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Contact List Section */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-yellow-500 flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          Kontaktliste ({emailContacts.length} Kontakte)
                        </h3>
                        
                        {/* CSV Upload */}
                        <div className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                          <Label className="text-gray-300 mb-2 block">CSV-Liste hochladen</Label>
                          <p className="text-gray-500 text-sm mb-3">Format: email,name,company (eine Zeile pro Kontakt)</p>
                          <input 
                            type="file" 
                            accept=".csv" 
                            ref={csvInputRef}
                            onChange={handleUploadCSV}
                            className="hidden"
                          />
                          <Button 
                            onClick={() => csvInputRef.current?.click()}
                            variant="outline"
                            className="border-yellow-600/50 text-yellow-500 hover:bg-yellow-600/20"
                          >
                            <Upload className="h-4 w-4 mr-2" /> CSV hochladen
                          </Button>
                        </div>
                        
                        {/* Add Single Contact */}
                        <div className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                          <Label className="text-gray-300 mb-2 block">Einzelnen Kontakt hinzufügen</Label>
                          <div className="space-y-2">
                            <Input
                              placeholder="E-Mail *"
                              value={newContact.email}
                              onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                            />
                            <Input
                              placeholder="Name"
                              value={newContact.name}
                              onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                            />
                            <Input
                              placeholder="Firma"
                              value={newContact.company}
                              onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                            />
                            <Button 
                              onClick={handleAddContact}
                              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                            >
                              <UserPlus className="h-4 w-4 mr-2" /> Kontakt hinzufügen
                            </Button>
                          </div>
                        </div>
                        
                        {/* Contact List */}
                        <div className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50 max-h-64 overflow-y-auto">
                          {emailContacts.length > 0 ? (
                            <div className="space-y-2">
                              {emailContacts.map((contact) => (
                                <div key={contact.id} className="flex justify-between items-center p-2 bg-gray-900 rounded">
                                  <div>
                                    <p className="text-white text-sm">{contact.email}</p>
                                    <p className="text-gray-400 text-xs">{contact.name || 'Kein Name'} {contact.company ? `- ${contact.company}` : ''}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteContact(contact.id)}
                                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">Keine Kontakte vorhanden</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Campaign Section */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-yellow-500 flex items-center gap-2">
                          <Send className="h-5 w-5" />
                          Kampagne versenden
                        </h3>
                        
                        <div className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50 space-y-4">
                          <div>
                            <Label className="text-gray-300">Betreff *</Label>
                            <Input
                              placeholder="E-Mail Betreff eingeben..."
                              value={campaignSubject}
                              onChange={(e) => setCampaignSubject(e.target.value)}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-gray-300">Vorlage auswählen *</Label>
                            <select
                              value={selectedTemplate}
                              onChange={(e) => setSelectedTemplate(e.target.value)}
                              className="w-full bg-gray-800 border border-yellow-600/20 text-white rounded-md p-2"
                            >
                              <option value="">Vorlage wählen...</option>
                              {emailTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <Label className="text-gray-300">Test-E-Mail (optional)</Label>
                            <Input
                              placeholder="test@example.com"
                              value={testEmail}
                              onChange={(e) => setTestEmail(e.target.value)}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                            />
                            <p className="text-gray-500 text-xs mt-1">Zuerst an diese Adresse senden, um zu testen</p>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleSendCampaign(true)}
                              disabled={isSendingCampaign || !testEmail || !selectedTemplate || !campaignSubject}
                              variant="outline"
                              className="flex-1 border-yellow-600/50 text-yellow-500 hover:bg-yellow-600/20"
                            >
                              Test senden
                            </Button>
                            <Button
                              onClick={() => handleSendCampaign(false)}
                              disabled={isSendingCampaign || emailContacts.length === 0 || !selectedTemplate || !campaignSubject}
                              className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                            >
                              {isSendingCampaign ? 'Wird gesendet...' : `An ${emailContacts.length} senden`}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Available Templates with Edit/Preview */}
                        <div className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                          <h4 className="text-gray-300 font-medium mb-3">Verfügbare Vorlagen</h4>
                          {emailTemplates.length > 0 ? (
                            <div className="space-y-3">
                              {emailTemplates.map((template) => (
                                <div key={template.id} className="flex justify-between items-center p-3 bg-gray-900 rounded border border-gray-700">
                                  <div>
                                    <p className="text-white text-sm font-medium">{template.name}</p>
                                    <p className="text-gray-400 text-xs">{template.description || 'Keine Beschreibung'}</p>
                                    {template.variables && template.variables.length > 0 && (
                                      <p className="text-gray-500 text-xs mt-1">
                                        Variablen: {template.variables.map(v => `{{${v}}}`).join(', ')}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                      onClick={() => handlePreviewTemplate(template.id)}
                                    >
                                      <Eye className="h-4 w-4 mr-1" /> Vorschau
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                                      onClick={() => handleEditTemplate(template)}
                                    >
                                      <Edit className="h-4 w-4 mr-1" /> Bearbeiten
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-2">Keine Vorlagen verfügbar</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Template Edit Dialog */}
                    {editingTemplate && (
                      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                        <div className="bg-gray-900 border border-yellow-600/30 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
                          <div className="p-4 border-b border-yellow-600/20 flex justify-between items-center">
                            <h3 className="text-yellow-500 font-semibold text-lg">
                              Vorlage bearbeiten: {editingTemplate.name}
                            </h3>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingTemplate(null); setTemplateContent(''); }}
                              className="text-gray-400 hover:text-white"
                            >
                              ✕
                            </Button>
                          </div>
                          <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                            <div className="text-gray-400 text-sm mb-2">
                              <strong>Verfügbare Variablen:</strong> {editingTemplate.variables?.map(v => `{{${v}}}`).join(', ') || 'Keine'}
                            </div>
                            <textarea
                              value={templateContent}
                              onChange={(e) => setTemplateContent(e.target.value)}
                              className="w-full h-96 bg-gray-800 text-white border border-yellow-600/20 rounded p-3 font-mono text-sm"
                              placeholder="HTML-Inhalt der Vorlage..."
                            />
                            <div className="flex gap-3">
                              <Button
                                onClick={handleSaveTemplate}
                                disabled={isSavingTemplate}
                                className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                              >
                                {isSavingTemplate ? 'Speichern...' : 'Speichern'}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handlePreviewTemplate(editingTemplate.id)}
                                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                              >
                                Vorschau öffnen
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() => { setEditingTemplate(null); setTemplateContent(''); }}
                                className="text-gray-400 hover:text-white"
                              >
                                Abbrechen
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Certificates Tab */}
              <TabsContent value="certificates">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Award className="h-5 w-5 text-yellow-500" />
                      Alle Zertifikate ({allCertificates.length})
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Übersicht aller ausgestellten Zertifikate mit PDF-Download
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {allCertificates.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-yellow-600/20">
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Zertifikat-Nr.</th>
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Mitarbeiter</th>
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Firma</th>
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Kurs</th>
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Gültig bis</th>
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Status</th>
                                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Aktionen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allCertificates.map((cert, index) => {
                                const isExpired = new Date(cert.expiryDate) < new Date();
                                const expiryDate = new Date(cert.expiryDate).toLocaleDateString('de-DE');
                                return (
                                  <tr key={cert.id || cert.certificateNumber || index} className="border-b border-gray-800 hover:bg-gray-800/50">
                                    <td className="py-3 px-4">
                                      <span className="text-yellow-500 font-mono text-sm">{cert.certificateNumber}</span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className="text-white">{cert.employeeName || 'Unbekannt'}</span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className="text-gray-400">{cert.companyName || 'Unbekannt'}</span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className="text-gray-300">{cert.courseTitle || 'Unbekannter Kurs'}</span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className={isExpired ? 'text-red-400' : 'text-green-400'}>{expiryDate}</span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className={`px-2 py-1 rounded text-xs font-semibold ${isExpired ? 'bg-red-900/50 text-red-400' : 'bg-green-900/50 text-green-400'}`}>
                                        {isExpired ? 'Abgelaufen' : 'Gültig'}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={async () => {
                                            const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/certificates/admin/${cert.certificateNumber}/download`;
                                            
                                            // Fetch PDF and open in new tab with blob URL
                                            try {
                                              const response = await apiFetch(pdfUrl);
                                              const blob = await response.blob();
                                              const blobUrl = URL.createObjectURL(blob);
                                              window.open(blobUrl, '_blank');
                                            } catch (err) {
                                              console.error('PDF load error:', err);
                                              // Fallback to direct download
                                              window.open(pdfUrl, '_blank');
                                            }
                                          }}
                                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                        >
                                          <Eye className="h-4 w-4 mr-1" /> Ansehen
                                        </Button>
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            // Force download with anchor tag
                                            const link = document.createElement('a');
                                            link.href = `${process.env.REACT_APP_BACKEND_URL}/api/certificates/admin/${cert.certificateNumber}/download`;
                                            link.download = `Zertifikat_${cert.certificateNumber}.pdf`;
                                            link.target = '_blank';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                          }}
                                          className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                                        >
                                          <FileText className="h-4 w-4 mr-1" /> Download
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Award className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">Noch keine Zertifikate vorhanden</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Legal Pages Tab */}
              <TabsContent value="legal">
                <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Scale className="h-5 w-5 text-yellow-500" />
                      Rechtliche Seiten
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Impressum, AGB und Datenschutzerklärung bearbeiten – die Änderungen sind nach dem Speichern sofort auf den öffentlichen Seiten <code className="text-yellow-400">/legal/...</code> aktiv.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" data-testid="legal-list">
                      <LegalDocumentRow
                        type="impressum"
                        title="Impressum"
                        subtitle="Angaben gemäß § 5 TMG"
                        page={legalPages.impressum}
                        onSaved={loadData}
                      />
                      <LegalDocumentRow
                        type="agb"
                        title="Allgemeine Geschäftsbedingungen (AGB)"
                        subtitle="Vertragsbedingungen für Kunden"
                        page={legalPages.agb}
                        onSaved={loadData}
                      />
                      <LegalDocumentRow
                        type="datenschutz"
                        title="Datenschutzerklärung"
                        subtitle="Informationen zum Datenschutz (DSGVO)"
                        page={legalPages.datenschutz}
                        onSaved={loadData}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tracking & Cookies Tab */}
              <TabsContent value="tracking">
                <TrackingSettingsTab />
              </TabsContent>

              {/* Companies Tab */}
              <TabsContent value="companies">
                <CompaniesTab companies={companies} />
              </TabsContent>

              {/* Enrollments Tab */}
              <TabsContent value="enrollments">
                <EnrollmentsTab enrollments={enrollments} />
              </TabsContent>

              {/* Sales & Revenue Tab */}
              <TabsContent value="sales">
                <SalesOrdersPanel />
              </TabsContent>

              {/* Schulungswünsche Tab */}
              <TabsContent value="wishes">
                <TrainingWishesTab />
              </TabsContent>

              {/* Analytics & Drop-offs Tab */}
              <TabsContent value="analytics">
                <AdminAnalyticsPanel />
              </TabsContent>

              {/* Sora-2 Video-Intros Tab */}
              <TabsContent value="video-intros">
                <AdminVideoIntrosPanel />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
